import buildSiteplanStatsHtml from '../services/siteplanStatsService';
import LotStatus from '../enums/lotStatus';

/**
 * Renders a single floor-plan card.
 * @param {Object} card - Floor-plan card data.
 * @returns {string} HTML string for one card.
 */
export function renderFloorPlanCard(card = {}) {
	const {
		imageAlt = card.name?.trim()
			? `${card.name.trim()} floor plan hero image`
			: 'floor plan hero image',
		lotLabel =  card.lotIds?.length ? 'Lot' : null,
		lotDisplayLabel = null,
		statusLabel = null,
	} = card;

	// NHS20-9017: a Model home plan shows "Model home" in place of its price, everywhere else.
	const isModel = card.lotStatus === LotStatus.Model.toString();

	const lotCount = card.lotIds?.length ?? 0;
	const lotText = lotCount === 1 ? 'Lot' : 'Lots';
	// Model home cards carry a single lot's displayLabel and show that instead of a lot count.
	const resolvedLotLabel = lotDisplayLabel
		? `Lot ${lotDisplayLabel}`
		: (lotLabel && lotCount > 0 && `${lotCount} ${lotText} available`) || null;

	const lotBadgeHtml = resolvedLotLabel
		? `<div class="nhs-c-siteplan-card__badge">${resolvedLotLabel}</div>`
		: '';

	const statusBadgeHtml = statusLabel
		? `<div class="nhs-c-siteplan-card__badge nhs-c-siteplan-card__badge--available">${statusLabel}</div>`
		: '';
	const badgesHtml = lotBadgeHtml || statusBadgeHtml
		? `
			<div class="nhs-c-siteplan-card__badges" data-qa="native_ism_floorplan_badge">
				${lotBadgeHtml ? `<div class="nhs-c-siteplan-card__badge nhs-mq-siteplan-card__badge-medium">${resolvedLotLabel}</div>` : ''}
				${statusBadgeHtml ? `<div class="nhs-c-siteplan-card__badge nhs-c-siteplan-card__badge--available nhs-mq-siteplan-card__badge-medium">${statusLabel}</div>` : ''}
			</div>
		`
		: '';

	const statsHtml = buildSiteplanStatsHtml([
		{ value: card.bedrooms, label: 'Bedrooms', suffix: 'Bd', isNumeric: true },
		{ value: card.formattedBathrooms, label: 'Baths', suffix: 'Ba', isNumeric: true },
		{ value: card.garage, label: 'Garage', suffix: 'Gr', isNumeric: true },
		{ value: card.formattedSquareFeet, label: 'Square Feet', suffix: 'SqFt' },
	]);

	return `
		<article class="nhs-c-siteplan-card" data-plan-id="${card.planId}" data-qa="native_ism_floorplan_card">
			<div class="nhs-c-siteplan-card__image-wrapper nhs-mq-siteplan-card__image-wrapper-medium">
				<img class="nhs-c-siteplan-card__image" data-siteplan-card-image 
                    loading="lazy"
                    src="${card.image}"
                    alt="${imageAlt}"
                    data-qa="native_ism_floorplan_card_image" />
			</div>
			${badgesHtml}
			<div class="nhs-c-siteplan-card__content">
			<h4 class="nhs-c-siteplan-card__title nhs-mq-siteplan-card__title-medium" data-qa="native_ism_floorplan_title">${card.name}</h4>
			<p class="nhs-c-siteplan-card__price nhs-mq-siteplan-card__price-medium" data-qa="native_ism_floorplan_price">${isModel ? 'Model home' : card.formattedPrice}</p>
				${statsHtml ? `<div class="nhs-c-siteplan-card__stats" data-qa="native_ism_floorplan_stats">${statsHtml}</div>` : ''}
			</div>
		</article>
	`;
}

/**
 * Renders the floor-plan cards grid.
 * @param {Object[]} cards - Floor-plan card list.
 * @returns {string} HTML string for the cards grid.
 */
export function renderFloorPlanGrid(cards = []) {
	const cardsHtml = cards.map((card) => renderFloorPlanCard(card)).join('');
	const gridClasses = [
		'nhs-c-native-interactive-site-map__card-grid',
		'nhs-mq-native-interactive-site-map__card-grid-medium',
		'nhs-mq-native-interactive-site-map__card-grid-large',
	];

	if (cards.length > 1) {
		gridClasses.push('nhs-c-native-interactive-site-map__card-grid--scroll');
	}

	return `
		<div class="${gridClasses.join(' ')}" data-qa="native_ism_floorplan_grid">
		${cardsHtml}
		</div>
    `;
}
